/*
 *   File : change-fund-allocation.js
 *   Author : https://evoqins.com
 *   Description : Change basket fund and allocation modal
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useState } from "react";
import { toast } from "react-toastify";

// *********** Components ***************//
import { PrimaryButton, SecondaryButton } from "../Buttons";
import { CustomTextInput } from "../Form-Elements";
import { Icon } from "../Icon";
import { EmptyScreen } from "../Other";
import { APIService } from "../../Service";

// *********** Assets ***************//
import Close from "../../Assets/Images/close-btn.svg";
import CloseWhite from "../../Assets/Images/close-btn-white.svg";
import SuccessImage from "../../Assets/Images/success.webp";
import Loader from "../../Assets/Images/coloured_loader.svg";
import EmptyFunds from "../../Assets/Images/EmptyScreen/no-data.webp";
import InfoIcon from "../../Assets/Images/GoalNew/fund-info.svg";
import AddIcon from "../../Assets/Images/GoalNew/fund-add.svg";

// *********** Styles ***************//
import Colors from "../../Style/color.scss";

const PAGE_SIZE = 50;
const SCHEME_SORT = 3;

const UPDATED_MESSAGE = {
    title: "Fund changes updated",
    description: "Your fund selection and allocation have been saved.",
};

const DELETED_MESSAGE = {
    title: "Fund deleted successfully",
    description: "This fund has been successfully deleted",
};

const FUND_CATEGORIES = [
    { id: "GOLD/SILVER", name: "Gold & Silver" },
    { id: "EQUITY", name: "Equity" },
    { id: "DEBT", name: "Debt" },
];

function ChangeFundAllocationModal(props) {

    const mappedFunds = (props.funds || []).map((fund) => {
        return {
            mf_id: fund.mf_id,
            scheme_name: fund.scheme_name,
            allocation: fund.allocation_percentage ?? fund.percentage ?? fund.allocation,
        };
    });
    const [funds, setFunds] = useState(mappedFunds);
    const [fundOptions, setFundOptions] = useState([]);
    const [isSelectingFund, setIsSelectingFund] = useState(false);
    const [editingFundId, setEditingFundId] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(FUND_CATEGORIES[0].id);
    const [fundIdToDelete, setFundIdToDelete] = useState(null);
    const [isFundDeleted, setIsFundDeleted] = useState(false);

    // The modal shows the fund list, the delete confirmation or a success after a delete or the update
    const isSuccessView = props.isUpdated === true || isFundDeleted;
    const isFundListView = isSuccessView === false && fundIdToDelete === null;
    const successMessage = isFundDeleted ? DELETED_MESSAGE : UPDATED_MESSAGE;

    const totalAllocation = funds.reduce((total, fund) => total + Number(fund.allocation || 0), 0);

    const _handleAllocationChange = (fund_id, value) => {
        if (value !== "" && Number(value) > 100) {
            return;
        }

        const next_funds = funds.map((fund) => {
            if (fund.mf_id !== fund_id) {
                return fund;
            }

            return {
                ...fund,
                allocation: value,
            };
        });

        setFunds(next_funds);
    };

    const _handleChangeFund = (fund_id) => {
        setEditingFundId(fund_id);
        setIsSelectingFund(true);
        setSearchValue("");
        setSelectedCategoryId(FUND_CATEGORIES[0].id);
        setIsSearching(false);
        _getSchemeList("", FUND_CATEGORIES[0].id, false);
    };

    // A new fund is picked the same way as a changed one, it has no fund to replace
    const _handleAddFund = () => {
        _handleChangeFund(null);
    };

    const _handleAskDeleteFund = (fund_id) => {
        setFundIdToDelete(fund_id);
    };

    const _handleCancelDeleteFund = () => {
        setFundIdToDelete(null);
    };

    const _handleConfirmDeleteFund = () => {
        setFunds(funds.filter((fund) => fund.mf_id !== fundIdToDelete));
        setFundIdToDelete(null);
        setIsFundDeleted(true);
    };

    // After a delete the success only goes back to the fund list, the update is still to be done
    const _handleCloseSuccess = () => {
        if (isFundDeleted) {
            setIsFundDeleted(false);
            return;
        }

        props.onClose();
    };

    const _handleBackToAllocation = () => {
        setIsSearching(false);
        setIsSelectingFund(false);
    };

    const _handleSearch = (value) => {
        setSearchValue(value);

        if (value.trim() === "") {
            _getSchemeList("", selectedCategoryId, true);
        }
    };

    const _handleClearSearch = () => {
        setSearchValue("");
        _getSchemeList("", selectedCategoryId, true);
    };

    const _handleSearchKeyDown = (event) => {
        if (event.key !== "Enter") {
            return;
        }

        _getSchemeList(searchValue.trim(), selectedCategoryId, true);
    };

    const _handleSelectCategory = (category) => {
        setSelectedCategoryId(category.id);
        _getSchemeList(searchValue.trim(), category.id, true);
    };

    const _handleSelectFund = (selected_fund) => {
        const is_already_added = funds.some((fund) => fund.mf_id === selected_fund.mf_id && fund.mf_id !== editingFundId);

        if (is_already_added) {
            toast.dismiss();
            toast.error("This fund is already added", {
                className: "e-toast",
            });
            return;
        }

        if (editingFundId === null) {
            setFunds([...funds, { mf_id: selected_fund.mf_id, scheme_name: selected_fund.scheme_name, allocation: "" }]);
            setIsSelectingFund(false);
            return;
        }

        const next_funds = funds.map((fund) => {
            if (fund.mf_id !== editingFundId) {
                return fund;
            }

            return {
                ...fund,
                mf_id: selected_fund.mf_id,
                scheme_name: selected_fund.scheme_name,
            };
        });

        setFunds(next_funds);
        setIsSelectingFund(false);
    };

    // API - Get scheme list
    const _getSchemeList = (scheme_name, fund_category, show_loader) => {
        let api_version = 2;
        let is_post = true;
        let url = "scheme/list";
        let data = {
            "page_num": 1,
            "page_size": PAGE_SIZE,
            "scheme_name": scheme_name,
            "fund_category": fund_category,
            "sort": SCHEME_SORT,
        };

        if (show_loader) {
            setIsSearching(true);
        }

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                setFundOptions(response.data?.funds || []);
            } else {
                setFundOptions([]);
            }

            if (show_loader) {
                setIsSearching(false);
            }
        });
    };

    useEffect(() => {
        _getSchemeList("", FUND_CATEGORIES[0].id, false);
    }, []);

    const _handleUpdateFunds = () => {
        if (totalAllocation !== 100) {
            toast.dismiss();
            toast.error("Total allocation should be 100%", {
                className: "e-toast",
            });
            return;
        }

        props.onUpdate(funds);
    };

    const _renderFundOptions = () => {
        if (isSearching) {
            return (
                <div className=" text-center mt-5">
                    <img src={Loader}
                        draggable={false}
                        alt="loader" />
                </div>
            );
        }

        if (fundOptions.length === 0) {
            return (
                <EmptyScreen
                    img={EmptyFunds}
                    title="No funds found !"
                    description="There is nothing to show right now." />
            );
        }

        return fundOptions.map((fund_option) => (
            <button type="button"
                key={fund_option.mf_id}
                className="w-100 text-start bg-transparent e-border-1px-bright-gray e-border-radius-16 p-3 mb-3"
                onClick={() => _handleSelectFund(fund_option)}>
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex gap-2 min-width-0">

                        <img src={fund_option.image}
                            width={32}
                            height={32}
                            className="e-border-radius-8 object-fit-contain flex-shrink-0 e-border-1px-bright-gray p-1"
                            alt={fund_option.scheme_name} />

                        <div className="min-width-0">
                            <p className="mb-2 e-text-dark-jet e-font-12 e-alt-font-poppins e-font-weight-500 line-height-16px pe-lg-5">
                                {fund_option.scheme_name}
                            </p>
                            <p className="mb-0 d-flex align-items-center flex-wrap gap-1 e-text-sonic-silver e-font-12 e-alt-font-poppins e-font-weight-400 line-height-16px">
                                {fund_option.fund_category}
                                {
                                    fund_option.option_name ?
                                        <>
                                            <Icon icon="bullet"
                                                size={6}
                                                color={Colors.sonic_silver} />
                                            {fund_option.option_name}
                                        </>
                                        :
                                        null
                                }
                                {
                                    fund_option.risk ?
                                        <span className={`d-inline-flex align-items-center gap-1 ${fund_option.risk === "Risk Moderate" ? "e-text-mustard-yellow" : ""} ${fund_option.risk === "Risk High" ? "e-text-red" : ""} ${fund_option.risk === "Risk Low" ? "e-text-ufo-green" : ""}`}>
                                            <Icon icon="bullet"
                                                size={6}
                                                color="currentColor" />
                                            {fund_option.risk}
                                        </span>
                                        :
                                        null
                                }
                            </p>
                        </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                        <p className={`mb-2 d-flex align-items-center justify-content-end gap-1 e-font-12 e-alt-font-poppins e-font-weight-500 line-height-20px ${fund_option.returns?.returns_percentage >= 0 ? "e-text-ufo-green" : "e-text-red"}`}>
                            {fund_option.returns?.returns_percentage_text}
                            {
                                fund_option.returns?.returns_percentage >= 0 &&
                                <Icon icon="arrow-up-green"
                                    size={16} />
                            }
                        </p>
                        <p className="mb-0 e-text-bold-grey e-font-12 e-alt-font-poppins e-font-weight-400 line-height-16px">
                            {fund_option.returns?.tenure_text} returns
                        </p>
                    </div>
                </div>
            </button>
        ));
    };

    return (
        <div className="modal fade e-manage-modal change-fund-allocation-modal"
            id="change-fund-allocation-modal"
            tabIndex="-1"
            data-bs-backdrop="static"
            aria-labelledby="change-fund-allocation-label"
            aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">

                {/* Delete confirmation */}
                {
                    isSuccessView === false && fundIdToDelete !== null &&
                    <div className="modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow">
                        <div className="modal-header border-0 justify-content-between align-items-center pt-4 px-3 px-sm-4 pb-0">
                            <h6 className="mb-0 e-text-charleston-green e-alt-font-poppins e-font-weight-600 e-font-16 line-height-24px">
                                Delete fund?
                            </h6>
                            <img src={Close}
                                draggable={false}
                                className="cursor-pointer"
                                aria-label="Close"
                                alt="close"
                                width={24}
                                height={24}
                                onClick={_handleCancelDeleteFund} />
                        </div>
                        <div className="modal-body px-3 px-sm-4 pt-3 pb-4">
                            <p className="mb-0 e-text-dark-liver e-alt-font-poppins e-font-weight-500 e-font-14 line-height-20px">
                                Are you sure you want to delete this fund? This action cannot be undone.
                            </p>
                        </div>
                        <div className="modal-footer border-0 px-3 px-sm-4 pb-4 pt-0 gap-3 flex-nowrap">
                            <SecondaryButton name="No, cancel"
                                borderRadius="e-border-radius-10"
                                className="flex-fill w-50 padding-12px-top padding-12px-bottom e-bg-lotion e-font-16 e-text-charleston-green e-border-bright-gray m-0"
                                onPress={_handleCancelDeleteFund} />
                            <PrimaryButton name="Yes, Delete"
                                className="flex-fill w-50 padding-12px-top padding-12px-bottom m-0"
                                onPress={_handleConfirmDeleteFund} />
                        </div>
                    </div>
                }

                {/* Success */}
                {
                    isSuccessView &&
                    <div className="modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow">
                        <div className="modal-gradient-bg e-border-radius-24">
                            <div className="modal-header border-0 justify-content-end pt-4 px-3 px-sm-4 pb-0">
                                <img src={CloseWhite}
                                    id="change-fund-allocation-modal-close"
                                    draggable={false}
                                    className="cursor-pointer"
                                    data-bs-dismiss={isFundDeleted ? undefined : "modal"}
                                    aria-label="Close"
                                    alt="close"
                                    width={24}
                                    height={24}
                                    onClick={_handleCloseSuccess} />
                            </div>
                            <div className="modal-body d-flex flex-column align-items-center text-center px-3 px-sm-4 pt-0 pb-4">
                                <img src={SuccessImage}
                                    width={193}
                                    className="img-fluid"
                                    draggable={false}
                                    alt="" />
                                <p className="mt-4 mb-2 e-text-ufo-green e-alt-font-poppins e-font-weight-600 e-font-18 e-font-16-res">
                                    {successMessage.title}
                                </p>
                                <p className="mb-4 e-text-neutral-dark-gray e-alt-font-poppins e-font-weight-400 e-font-14 e-font-12-res">
                                    {successMessage.description}
                                </p>
                                <div className="col-12 col-sm-8">
                                    <PrimaryButton name="Done"
                                        dismiss={isFundDeleted ? undefined : "modal"}
                                        className="w-100 padding-12px-top padding-12px-bottom"
                                        onPress={_handleCloseSuccess} />
                                </div>
                            </div>
                        </div>
                    </div>
                }

                {
                    isFundListView &&
                    <div className={`modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow ${isSelectingFund ? "min-h90vh" : ""}`}>
                        <div className="modal-header justify-content-space-between align-items-center e-bg-white e-border-radius-24 pt-4 px-3 px-sm-4 pb-3 border-0">
                            <div className="d-flex align-items-center gap-3">
                                {
                                    isSelectingFund ?
                                        <button type="button"
                                            className="border-0 bg-transparent p-0"
                                            aria-label="Back"
                                            onClick={_handleBackToAllocation}>
                                            <Icon icon="back-icon"
                                                size={24} />
                                        </button>
                                        :
                                        <Icon icon="back-icon"
                                            size={24} />
                                }
                                <h6 id="change-fund-allocation-label"
                                    className="mb-0 e-font-14-res e-text-charleston-green e-font-16 e-alt-font-poppins e-font-weight-600 line-height-24px">
                                    {isSelectingFund ? "Select fund" : "Change fund & allocation"}
                                </h6>
                            </div>
                            <img src={Close}
                                id="change-fund-allocation-modal-close"
                                draggable={false}
                                className="cursor-pointer"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                alt="close"
                                width={24}
                                height={24}
                                onClick={props.onClose} />
                        </div>
                        <div className="modal-body px-3 px-sm-4 pt-0 pb-3">
                            {
                                isSelectingFund ?
                                    <>
                                        <div className="bg-white position-sticky top-0 z-3 pb-4">
                                            <div className="mb-3 position-relative"
                                                onKeyDown={_handleSearchKeyDown}>
                                                <CustomTextInput placeholder="Search fund"
                                                    className="search-fund-input no-outline px-3"
                                                    height="h-auto"
                                                    wrapperClassName="e-border-1px-bright-gray no-margin-bottom"
                                                    inputWrapperBorderStyle="e-border-radius-16"
                                                    autoComplete="off"
                                                    errorMsg=""
                                                    value={searchValue}
                                                    handleChange={_handleSearch} />
                                                <Icon icon="fund-search-close"
                                                    size={24}
                                                    className={`${searchValue.length === 0 ? "opacity-0 pe-none" : "cursor-pointer"} position-absolute top-50 translate-middle-y end-0 me-2 me-sm-3`}
                                                    onClick={_handleClearSearch} />
                                            </div>

                                            <div className="d-flex gap-2 gap-sm-3 flex-wrap">
                                                {FUND_CATEGORIES.map((type) => (
                                                    <span key={type.id}
                                                        className={`${selectedCategoryId === type.id
                                                            ? "e-bg-egyptian-blue e-text-white"
                                                            : "e-bg-alice-blue_2 e-text-raisin-black"
                                                            } cursor-pointer d-flex align-items-center justify-content-center px-2 px-sm-3 py-1 py-sm-2 e-border-radius-32 e-font-12-res e-font-12 e-alt-font-poppins e-font-weight-500`}
                                                        onClick={() => _handleSelectCategory(type)}>
                                                        {type.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {
                                            _renderFundOptions()
                                        }
                                    </>
                                    :
                                    <>
                                        <div className="d-flex gap-2 e-bg-cornsilk e-border-radius-16 p-3">
                                            <img src={InfoIcon}
                                                width={20}
                                                height={20}
                                                className="flex-shrink-0 width-12px-res height-12px-res"
                                                draggable={false}
                                                alt="" />
                                            <p className="mb-0 e-text-ochre e-alt-font-poppins e-font-weight-400 e-font-10-res e-font-12 line-height-16px">
                                                Total allocation must always equal 100%. Make sure the percentages assigned to all funds add up to 100% before proceeding.
                                            </p>
                                        </div>

                                        <p className="d-flex justify-content-end align-items-baseline gap-1 mt-3 mb-3 e-text-charleston-green e-primary-font-inter line-height-24px">
                                            <span className="e-font-weight-400 e-font-12">Total allocation</span>
                                            <span className="e-font-weight-600 e-font-14">{totalAllocation}%</span>
                                        </p>

                                        {
                                            funds.map((fund, index) => (
                                                <div className="position-relative e-bg-cultured e-border-radius-16 p-3 mb-3"
                                                    key={fund.mf_id}>
                                                    <p className="mb-2 e-text-charleston-green e-alt-font-poppins e-font-weight-400 e-font-12 line-height-14px">
                                                        Fund {index + 1}
                                                    </p>
                                                    {
                                                        funds.length > 1 &&
                                                        <button type="button"
                                                            className="position-absolute top-0 end-0 mt-2 me-3 d-flex p-0 border-0 bg-transparent cursor-pointer"
                                                            aria-label={`Remove fund ${index + 1}`}
                                                            onClick={() => _handleAskDeleteFund(fund.mf_id)}>
                                                            <Icon icon="delete-fund"
                                                                size={24} />
                                                        </button>
                                                    }
                                                    <div className="d-flex align-items-start justify-content-between gap-3">
                                                        <p className="mb-0 pt-1 min-width-0 e-text-charleston-green e-primary-font-inter e-font-weight-500 e-font-12-res e-font-14 line-height-20px">
                                                            {fund.scheme_name}
                                                        </p>
                                                        <CustomTextInput variant={4}
                                                            type="number"
                                                            suffix="%"
                                                            suffixClassName="e-text-charleston-green e-font-weight-500 e-font-14 line-height-18px"
                                                            inputFontSize="e-font-14"
                                                            wrapperPadding="padding-12px-lr"
                                                            value={fund.allocation}
                                                            errorMsg=""
                                                            maxLength={3}
                                                            placeholder="0"
                                                            hasBottomMargin={false}
                                                            borderClassName="e-border-1px-sonic-silver"
                                                            wrapperClassName="w-70px h-40 flex-shrink-0 overflow-hidden"
                                                            inputWrapperBorderStyle="e-border-radius-12"
                                                            className="min-width-0 py-0"
                                                            handleChange={(value) => _handleAllocationChange(fund.mf_id, value)} />
                                                    </div>
                                                    <SecondaryButton name="Change Fund"
                                                        type={2}
                                                        icon="change-fund-icon"
                                                        size={16}
                                                        className="d-flex justify-content-center gap-1 align-items-center w-100 padding-12px-top e-border-radius-10 padding-12px-bottom mt-3 change-fund-btn e-font-12-res e-font-14 e-font-weight-600"
                                                        onPress={() => _handleChangeFund(fund.mf_id)} />
                                                </div>
                                            ))
                                        }

                                        <div className="text-center">
                                            <button type="button"
                                                className="d-inline-flex align-items-center justify-content-center gap-2 p-2 bg-transparent e-border-1px-egyptian-blue e-border-radius-8 e-text-egyptian-blue e-primary-font-inter e-font-weight-500 e-font-12-res e-font-14 line-height-20px cursor-pointer"
                                                onClick={_handleAddFund}>
                                                <img src={AddIcon}
                                                    width={16}
                                                    height={16}
                                                    draggable={false}
                                                    alt="" />
                                                Add fund
                                            </button>
                                        </div>
                                    </>
                            }
                        </div>
                        {
                            isSelectingFund === false &&
                            <div className="modal-footer border-0 e-bg-white px-3 px-sm-4 pb-4 pt-3 gap-3 flex-nowrap">
                                <SecondaryButton name="Cancel"
                                    dismiss="modal"
                                    borderRadius="e-border-radius-10"
                                    className="flex-fill w-50 padding-12px-top padding-12px-bottom e-bg-lotion e-font-12-res e-font-16 e-text-charleston-green e-border-bright-gray m-0"
                                    onPress={props.onClose} />
                                <PrimaryButton name="Update"
                                    className="flex-fill w-50 padding-12px-top e-font-12-res padding-12px-bottom m-0"
                                    loading={props.loading}
                                    disabled={props.loading}
                                    onPress={_handleUpdateFunds} />
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    );
}

export default ChangeFundAllocationModal;
